import React, { useEffect, useRef, useState, useCallback } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { Loader2, Activity, MoonStar, Sun, Wifi, AlertCircle } from "lucide-react";

/**
 * Phone Sentinel — passive presence + sleep-window detector.
 *
 * Per No. 6 (20 May 2026): "Spy Health knows when I sleep so the panels
 * are honest." Instead of building the full home-network arc, we use the
 * phone the operator already carries — its accelerometer, its screen-on
 * events, and its mere presence in the foreground — as a sleep proxy.
 *
 * How it works (no flinching):
 *   - On first visit we provision a single "Phone Sentinel" device via the
 *     existing /api/shed/intake/devices flow. Token is stored in localStorage.
 *   - While the page is in the foreground we file an "awake" heartbeat every
 *     60s — these include the peak motion magnitude over the last minute.
 *   - On visibilitychange we file `screen_on` / `screen_off` events.
 *   - On `pagehide` we file a `paused` event so the sleep inferrer can mark
 *     the moment the operator put the phone down.
 *
 * Limitations (will not be flinched away from):
 *   - JS does NOT run when the screen is off, so we infer sleep from the
 *     gap between events, not by sampling during sleep itself.
 *   - This works as a sleep INDICATOR, not a polysomnogram. A long gap
 *     between 23:00 and 06:00 with no events = high-confidence sleep window.
 *   - If you don't visit the page at all, no data is filed — the page must
 *     be opened (or kept open as a pinned tab / installed PWA) to function.
 *
 * Recommended use: install SpyFinance as a PWA, leave Phone Sentinel as
 * the last tab you visit before sleep and the first one after waking.
 */
const DEVICE_KEY = "shed_phone_sentinel";  // localStorage: {device_id, token}
const PING_INTERVAL_MS = 60 * 1000;  // 60s heartbeats while foreground
const MOTION_THROTTLE_MS = 250;

export default function PhoneSentinel() {
    const [status, setStatus] = useState("idle");  // idle | provisioning | listening | error
    const [err, setErr] = useState("");
    const [device, setDevice] = useState(null);
    const [pingCount, setPingCount] = useState(0);
    const [motionEventCount, setMotionEventCount] = useState(0);
    const [lastPingAt, setLastPingAt] = useState(null);
    const [screenState, setScreenState] = useState("on");

    // Buffers
    const peakMotionRef = useRef(0);
    const lastMotionSampleRef = useRef(0);
    const pingTimerRef = useRef(null);

    // -----------------------------------------------------------------
    // Provisioning — auto-register a device the first time, otherwise
    // pull from localStorage. The device token is per-user-scoped on the
    // backend, so it survives sign-outs of the OAuth identity.
    // -----------------------------------------------------------------
    const provision = useCallback(async () => {
        try {
            setStatus("provisioning");
            const cached = JSON.parse(localStorage.getItem(DEVICE_KEY) || "null");
            if (cached?.device_id && cached?.token) {
                setDevice(cached);
                setStatus("listening");
                return cached;
            }
            const { data } = await api.post("/shed/intake/devices", {
                label: "Phone Sentinel",
                device_type: "phone-sentinel",
                default_domain: "health",
            });
            const rec = { device_id: data.id, token: data.token };
            localStorage.setItem(DEVICE_KEY, JSON.stringify(rec));
            setDevice(rec);
            setStatus("listening");
            return rec;
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Could not register the sentinel");
            setStatus("error");
            return null;
        }
    }, []);

    // -----------------------------------------------------------------
    // File an event via the per-device intake endpoint. We hit the public
    // intake route directly so the device token (not the user JWT) is
    // what authorises this — works even if the user logs out elsewhere.
    // -----------------------------------------------------------------
    const fileEvent = useCallback(async (rec, kind, structured = {}) => {
        if (!rec?.device_id || !rec?.token) return;
        try {
            const summary = {
                awake_ping: `Phone foreground · peak motion ${(structured.motion_peak_g || 0).toFixed(2)}g`,
                screen_on: "Phone screen on",
                screen_off: "Phone screen off",
                paused: "Phone Sentinel page hidden",
            }[kind] || `Phone Sentinel event: ${kind}`;
            await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/shed/intake/${rec.device_id}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Device-Token": rec.token,
                    },
                    body: JSON.stringify({
                        summary,
                        domain: "health",
                        structured: {
                            event: kind,
                            screen_state: kind === "screen_off" ? "off" : "on",
                            ...structured,
                        },
                        tags: ["phone-sentinel", kind],
                        confidence: 0.95,
                    }),
                    // keepalive lets the request survive a pagehide
                    keepalive: ["paused", "screen_off"].includes(kind),
                },
            );
            setPingCount((n) => n + 1);
            setLastPingAt(new Date());
        } catch (_) { /* don't break the loop on a single failed ping */ }
    }, []);

    // -----------------------------------------------------------------
    // The motion sample — devicemotion fires fast (60Hz on most phones).
    // Throttle to ~4Hz and accumulate the peak so the awake ping carries
    // a meaningful 1-minute summary.
    // -----------------------------------------------------------------
    useEffect(() => {
        if (status !== "listening") return undefined;
        const onMotion = (ev) => {
            const now = Date.now();
            if (now - lastMotionSampleRef.current < MOTION_THROTTLE_MS) return;
            lastMotionSampleRef.current = now;
            const a = ev.accelerationIncludingGravity || ev.acceleration || {};
            const mag = Math.sqrt(
                (a.x || 0) ** 2 + (a.y || 0) ** 2 + (a.z || 0) ** 2,
            ) / 9.81; // gs
            if (mag > peakMotionRef.current) {
                peakMotionRef.current = mag;
                setMotionEventCount((n) => n + 1);
            }
        };
        window.addEventListener("devicemotion", onMotion);
        return () => window.removeEventListener("devicemotion", onMotion);
    }, [status]);

    // -----------------------------------------------------------------
    // Heartbeat loop — every 60s, file an "awake_ping" with the peak
    // motion since the last ping, then reset the peak.
    // -----------------------------------------------------------------
    useEffect(() => {
        if (status !== "listening" || !device) return undefined;
        const tick = () => {
            const peak = peakMotionRef.current;
            peakMotionRef.current = 0;
            fileEvent(device, "awake_ping", { motion_peak_g: peak });
        };
        // Fire one immediately so the operator sees activity
        tick();
        pingTimerRef.current = setInterval(tick, PING_INTERVAL_MS);
        return () => {
            if (pingTimerRef.current) clearInterval(pingTimerRef.current);
        };
    }, [status, device, fileEvent]);

    // -----------------------------------------------------------------
    // Visibility + lifecycle events
    // -----------------------------------------------------------------
    useEffect(() => {
        if (status !== "listening" || !device) return undefined;
        const onVis = () => {
            if (document.visibilityState === "hidden") {
                setScreenState("off");
                fileEvent(device, "screen_off");
            } else {
                setScreenState("on");
                fileEvent(device, "screen_on");
            }
        };
        const onPagehide = () => fileEvent(device, "paused");
        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("pagehide", onPagehide);
        return () => {
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("pagehide", onPagehide);
        };
    }, [status, device, fileEvent]);

    // -----------------------------------------------------------------
    // iOS 13+ requires explicit permission for DeviceMotion. We expose a
    // button so the operator can grant it; on Android / desktop it just
    // works without the prompt.
    // -----------------------------------------------------------------
    const [motionPermission, setMotionPermission] = useState("default");
    const requestMotion = async () => {
        try {
            if (typeof DeviceMotionEvent !== "undefined"
                && typeof DeviceMotionEvent.requestPermission === "function") {
                const r = await DeviceMotionEvent.requestPermission();
                setMotionPermission(r);
            } else {
                setMotionPermission("not-needed");
            }
        } catch (e) {
            setMotionPermission("denied");
        }
    };

    // Auto-provision on mount
    useEffect(() => { provision(); }, [provision]);

    // Pull the most recent inferred sleep window from the new backend endpoint
    const [sleep, setSleep] = useState(null);
    const refreshSleep = useCallback(async () => {
        try {
            const { data } = await api.get("/sight/sleep-window");
            setSleep(data);
        } catch (_) { /* non-fatal */ }
    }, []);
    useEffect(() => {
        refreshSleep();
        const t = setInterval(refreshSleep, 60_000);
        return () => clearInterval(t);
    }, [refreshSleep]);

    return (
        <Shell>
            <div className="max-w-3xl">
                <div className="paper-card p-6 mb-6">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 border-2 border-stamp-red rounded-full flex items-center justify-center">
                            {status === "listening" ? (
                                <Activity className="w-6 h-6 text-stamp-red sf-glow" strokeWidth={2.4} />
                            ) : status === "provisioning" ? (
                                <Loader2 className="w-6 h-6 animate-spin text-ink-soft" />
                            ) : (
                                <AlertCircle className="w-6 h-6 text-stamp-red" />
                            )}
                        </div>
                        <div className="flex-1">
                            <div className="label-typed text-[9px]">Spy Health · Sleep Sentinel</div>
                            <h1 className="font-heading uppercase tracking-[0.06em] text-3xl text-ink mt-1">
                                Phone Sentinel
                                <span className="text-stamp-red text-base ml-2">●</span>
                            </h1>
                            <p className="font-body text-sm text-ink mt-2 leading-relaxed">
                                Files an &ldquo;awake&rdquo; heartbeat to the ledger every minute this
                                page is open in the foreground. Gaps between heartbeats &mdash; especially
                                between dusk and dawn &mdash; are inferred as sleep. Keep this page
                                open at night for honest panels.
                            </p>
                        </div>
                    </div>
                </div>

                {err && (
                    <div className="paper-card border-2 border-stamp-red p-4 mb-6" data-testid="phone-sentinel-error">
                        <div className="stamp-red font-heading text-sm tracking-[0.2em] mb-2">FAILURE</div>
                        <div className="font-body text-xs text-ink">{err}</div>
                    </div>
                )}

                {/* iOS permission prompt */}
                {typeof window !== "undefined"
                    && typeof window.DeviceMotionEvent !== "undefined"
                    && typeof window.DeviceMotionEvent.requestPermission === "function"
                    && motionPermission === "default" && (
                    <div className="paper-card border border-ink/20 p-4 mb-6">
                        <div className="label-typed text-[9px] mb-2">iOS permission required</div>
                        <p className="font-body text-sm text-ink mb-3">
                            iOS hides motion sensors behind a permission prompt. Tap to enable
                            &mdash; without this we can only track screen-on / screen-off, not
                            actual movement.
                        </p>
                        <button
                            onClick={requestMotion}
                            className="bg-ink text-paper-light font-heading uppercase tracking-[0.22em] text-xs py-2 px-4 hover:bg-stamp-red"
                            data-testid="phone-sentinel-grant-motion"
                        >
                            Grant motion access
                        </button>
                    </div>
                )}

                {/* Live status */}
                <div className="paper-card p-5 mb-6">
                    <div className="label-typed mb-3">Live status</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <Stat label="State" value={status === "listening" ? "ON AIR" : status.toUpperCase()} />
                        <Stat label="Heartbeats" value={pingCount} />
                        <Stat label="Motion events" value={motionEventCount} />
                        <Stat label="Screen" value={screenState.toUpperCase()} />
                    </div>
                    {lastPingAt && (
                        <div className="font-body text-[11px] text-ink-soft mt-3">
                            Last ping {lastPingAt.toLocaleTimeString()}
                        </div>
                    )}
                </div>

                {/* Sleep inference panel */}
                <div className="paper-card p-5 mb-6" data-testid="phone-sentinel-sleep-panel">
                    <div className="flex items-center gap-2 mb-3">
                        <MoonStar className="w-4 h-4 text-ink-soft" />
                        <div className="label-typed">Inferred sleep window (last 48h)</div>
                    </div>
                    {!sleep && (
                        <div className="font-body text-sm text-ink-soft">
                            Loading…
                        </div>
                    )}
                    {sleep && !sleep.found && (
                        <div className="font-body text-sm text-ink-soft">
                            No sleep window detected yet &mdash; need at least one quiet stretch of
                            two hours or more between events. Try again after a sleep cycle.
                        </div>
                    )}
                    {sleep && sleep.found && (
                        <div>
                            <div className="font-heading text-xl text-ink tracking-[0.04em]">
                                {sleep.duration_hours.toFixed(1)}h
                            </div>
                            <div className="font-body text-sm text-ink mt-1">
                                {sleep.start_local} &rarr; {sleep.end_local}
                            </div>
                            <div className="font-body text-[11px] text-ink-soft mt-2">
                                Confidence {Math.round(sleep.confidence * 100)}% &middot; based on
                                a {sleep.gap_minutes.toFixed(0)}-minute gap between filed events.
                            </div>
                        </div>
                    )}
                </div>

                {/* Operational notes */}
                <div className="paper-card p-5">
                    <div className="label-typed mb-2">Operational notes</div>
                    <ul className="font-body text-xs text-ink leading-relaxed space-y-1.5 list-disc pl-5">
                        <li className="flex items-start gap-1.5">
                            <Wifi className="w-3 h-3 mt-0.5 text-ink-soft shrink-0" />
                            <span>This page must be open for heartbeats to file. Install
                            SpyFinance as a PWA and keep this tab pinned.</span>
                        </li>
                        <li className="flex items-start gap-1.5">
                            <Sun className="w-3 h-3 mt-0.5 text-ink-soft shrink-0" />
                            <span>JS does not run while your phone is locked &mdash; sleep is
                            inferred from the silence between your last evening event and your
                            first morning event.</span>
                        </li>
                        <li className="flex items-start gap-1.5">
                            <MoonStar className="w-3 h-3 mt-0.5 text-ink-soft shrink-0" />
                            <span>The longest 2h+ gap inside the last 48h is treated as the
                            sleep window. Multiple gaps &mdash; e.g. a nap &mdash; are picked
                            by length; future iters can refine.</span>
                        </li>
                    </ul>
                </div>
            </div>
        </Shell>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="label-typed text-[9px]">{label}</div>
            <div className="font-heading text-base text-ink mt-1">{value}</div>
        </div>
    );
}
